import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";

import closeIcon from "assets/images/close-icon.svg";
import modalBgShade from "../../../assets/images/bg-modal-shade.svg";
import allImgPaths from "assets/images";
import { InputTextField } from "components/InputField";
import { useNavigate, useParams } from "react-router-dom";
import { cloneDeep, isEqual } from "lodash";
import { ACTUAL_TASK_NAMES, LOGISTICS_AND_OTHERS_ACTIONS_TABS, RFP_VENDOR_TYPES, VENDOR_OPTIONS } from "utils/constant";
import CustomSelect from "components/common/CustomSelect";
import CustomCheckBox from "components/CustomCheckBox";
import Toast from "components/common/Toast";
import useAppState from "context/useAppState";
import { differenceInDays } from "date-fns";
import { updateLogistics } from "services/chat";
import LogisticsViewMode from "./LogisticsViewMode";

const optionsVendor = VENDOR_OPTIONS.map((vo: any) => ({ title: vo.label, selected: false }));

const cuisineTypeOptions = [
    { listName: 'American', selected: false },
    { listName: 'Italian', selected: false },
    { listName: 'Chinese', selected: false },
    { listName: 'Pan-Asian', selected: false },
    { listName: 'Indian', selected: false },
    { listName: 'Mediterranean', selected: false },
    { listName: 'Continental', selected: false },
    { listName: 'Custom Menu', selected: false },
];

const dietaryRestrictionsOptions = [
    { listName: 'Vegetarian', selected: false },
    { listName: 'Vegan', selected: false },
    { listName: 'Halal', selected: false },
    { listName: 'Kosher', selected: false },
    { listName: 'Gluten-Free', selected: false },
    { listName: 'Lactose Free', selected: false },
    { listName: 'Nut Free', selected: false },
    { listName: 'Soy Free', selected: false },
    { listName: 'Shell Fish Free', selected: false },
    { listName: 'Egg Free', selected: false }
];

const beverageServiceOptions = [
    { listName: 'Alcoholic Beverages', selected: false },
    { listName: 'Non-Alcoholic Beverages', selected: false },
    { listName: 'In-House Bar with Bartender', selected: false },
    { listName: 'None', selected: false }
];

const DietaryRequirements = ({ data, totalEventDays, updateLogisticsState, isViewMode = false }: any) => {
    const initFormData: any = {
        cuisineTypes: cuisineTypeOptions,
        dietaryRestrictions: dietaryRestrictionsOptions,
        beverageServices: beverageServiceOptions,
        special_requirements: '',
        use_for: 'Entire Event',
        include_in_rfp: false,
        check_requirement: false,
        vendor: []
    }
    const { id: eventId } = useParams();
    const [formData, setFormData] = useState<any[]>([])
    const [saveBtnDisabled, setSaveBtnDisabled] = useState(true)
    let actualFormData = useRef<any>(cloneDeep([]));
    const { setState: setEventDataState, currentTaskObj } = useAppState('eventData');
    const { selectedCardDetail } = useAppState("cardWrapper");
    const [showAddNewButton, setShowAddNewButton] = useState(false);  // Var to focus on where to show the add new button

    useEffect(() => {
        setInitData()
    }, [])

    useEffect(() => {
        areArraysEqual(formData, actualFormData.current)
    }, [formData])

    const setInitData = () => {
        if (!data) {
            if (isViewMode) initFormData['isInit'] = true
            addExtraValues([initFormData], false)
        } else {
            addExtraValues(data, true)
        }
    }

    const addExtraValues = (data: any[], isEditMode: boolean) => {
        (data || []).map((dt: any) => {
            const selectedVendors = !dt?.vendor?.length ? optionsVendor : optionsVendor.map((ov: any) => {
                if (dt.vendor.includes(ov.title)) {
                    return { ...ov, selected: true };
                }
                return ov;
            });

            const selectedDays = (!dt?.use_for || dt?.use_for == 'Entire Event') ? totalEventDays : totalEventDays.map((td: any) => {
                if (dt.use_for.split(', ').includes(td.title)) {
                    return { ...td, isChecked: true };
                }
                return td;
            });

            dt['isEntireDay'] = !isEditMode ? true : (dt?.use_for == 'Entire Event' ? true : false);
            dt['totalDays'] = selectedDays;
            dt['showDropdownData'] = false;
            dt['showVendorDropdown'] = false;
            dt['isViewMode'] = isViewMode;
            dt['vendorOptions'] = selectedVendors
            return dt;
        })

        setFormData(data)
        actualFormData.current = cloneDeep(data)
    }

    const areArraysEqual = (original: any, cloned: any) => {
        const isSame = isEqual(original, cloned);
        setSaveBtnDisabled(isSame)
        setEventDataState({ unsavedChanges: !isSame })
        checkButtonStatus(original)
    };

    const handleVendorDropdownData = (formDataIndex: number) => {
        const clonedFormData = cloneDeep(formData);
        const currentValue = clonedFormData[formDataIndex]['showVendorDropdown'];
        clonedFormData[formDataIndex]['showVendorDropdown'] = !currentValue;
        setFormData(clonedFormData)
        actualFormData.current = cloneDeep(clonedFormData)
    }

    const checkButtonStatus = (data: any[]) => {
        const length = data.length;

        if (length != 1) {
            setShowAddNewButton(false)
            return
        }
        if (!data[0]?.isViewMode) {
            setShowAddNewButton(false)
            return
        };

        if (data[0]?.isInit) {
            setShowAddNewButton(true)
        } else {
            setShowAddNewButton(false)
        }
    }

    const handleDropdownData = (formDataIndex: number) => {
        const clonedFormData = cloneDeep(formData);
        const currentValue = clonedFormData[formDataIndex]['showDropdownData'];
        clonedFormData[formDataIndex]['showDropdownData'] = !currentValue;
        setFormData(clonedFormData)
        actualFormData.current = cloneDeep(clonedFormData)
    }

    const handleVendorChange = (index: number, name: string, value: any) => {
        const clonedFormData = cloneDeep(formData);
        clonedFormData[index][name] = value;
        clonedFormData[index]['include_in_rfp'] = true

        setFormData(clonedFormData)
    }

    const handleRFPChange = (index: number, name: string, value: any) => {
        const clonedFormData = cloneDeep(formData);

        clonedFormData[index][name] = value;
        const selectedVendor: any = value ? optionsVendor.find((ov: any) => ov.title.toLowerCase() == RFP_VENDOR_TYPES.FOOD_AND_BEVERAGE.toLowerCase()) : ''

        let updatedVendorOptions: any;
        if (!selectedVendor) {
            updatedVendorOptions = clonedFormData[index]['vendorOptions'].map((vo: any) => ({ ...vo, selected: false }))
            clonedFormData[index]['vendorOptions'] = updatedVendorOptions
            clonedFormData[index]['vendor'] = []
        } else {
            updatedVendorOptions = clonedFormData[index]['vendorOptions'].map((vo: any) => {
                vo['selected'] = vo.title.toLowerCase() == RFP_VENDOR_TYPES.FOOD_AND_BEVERAGE.toLowerCase();
                return vo
            })
            clonedFormData[index]['vendorOptions'] = updatedVendorOptions
            clonedFormData[index]['vendor'] = [selectedVendor?.title]
        }

        setFormData(clonedFormData)
    }

    const handleAddMore = () => {
        const clonedFormData = cloneDeep(formData);

        // When in view mode, and adding the very first block
        if (isViewMode && clonedFormData?.length == 1) {
            const savedValues = clonedFormData.find((cd: any) => !cd?.isSaved)
            if (savedValues) {
                clonedFormData[0]['isViewMode'] = false;
                clonedFormData[0]['isSaved'] = true;
                // clonedFormData[0]['isInit'] = false
                delete clonedFormData[0]?.isInit;
                setFormData(clonedFormData);
                actualFormData.current = cloneDeep(clonedFormData);

                return
            }
        }

        const clonedInitData = cloneDeep(initFormData)
        clonedInitData['isEntireDay'] = false;
        clonedInitData['totalDays'] = totalEventDays;
        clonedInitData['showDropdownData'] = false;
        clonedInitData['isViewMode'] = false;
        clonedInitData['vendorOptions'] = optionsVendor;
        clonedInitData['showVendorDropdown'] = false;

        clonedFormData.push(clonedInitData);
        setFormData(clonedFormData);
        // scrollHandler("transparent-scrollbar");

        setTimeout(() => {
            let divElement = document.getElementById('new-elements');
            if(divElement){
                let lastElement = divElement.lastElementChild;
                if(lastElement){
                    lastElement.scrollIntoView();
                    divElement.scrollTop -= 20;
                }
            }
        }, 100);
    }

    const scrollHandler = (elementId: any) => {
        let elementToGetScroll = document.getElementById("0");
        if (elementToGetScroll) {
            elementToGetScroll.scrollIntoView();
        }
    };

    // Remove Meeting
    const remove = (index: any) => {
        const newMeeting = cloneDeep(formData);
        newMeeting.splice(index, 1);
        setFormData(newMeeting);
    }

    const handleVendorSelection = (index: number, selected: boolean, formDataIndex: number) => {
        const clonedFormData = cloneDeep(formData)
        const updatedData = cloneDeep(clonedFormData[formDataIndex].vendorOptions)
        updatedData[index]['selected'] = selected;
        clonedFormData[formDataIndex]['vendorOptions'] = updatedData

        const selOpt = updatedData.filter((item: any) => item.selected).map((item: any) => item.title)

        clonedFormData[formDataIndex]['vendor'] = selOpt;
        clonedFormData[formDataIndex]['include_in_rfp'] = selOpt?.length ? true : false

        setFormData(clonedFormData)
    }

    const handleCheckboxOne = (isChecked: boolean, formDataIndex: number) => {
        const clonedFormData = cloneDeep(formData)
        clonedFormData[formDataIndex]['isEntireDay'] = isChecked

        if (isChecked) {
            const updatedData = cloneDeep(clonedFormData[formDataIndex].totalDays.map((item: any) => ({ ...item, isChecked: false })));
            clonedFormData[formDataIndex]['totalDays'] = updatedData
        }

        clonedFormData[formDataIndex]['use_for'] = 'Entire Event'

        setFormData(clonedFormData)
    }

    const handleCheckboxTwo = (index: number, isChecked: boolean, formDataIndex: number) => {
        const clonedFormData = cloneDeep(formData)
        const updatedData = cloneDeep(clonedFormData[formDataIndex].totalDays)
        updatedData[index]['isChecked'] = isChecked;
        clonedFormData[formDataIndex]['totalDays'] = updatedData

        const selOpt = updatedData.filter((item: any) => item.isChecked).map((item: any) => item.title).join(', ')
        const hasChecked = updatedData.some((day: any) => day.isChecked);
        if (hasChecked) {
            clonedFormData[formDataIndex]['isEntireDay'] = false
        }

        clonedFormData[formDataIndex]['use_for'] = selOpt

        setFormData(clonedFormData)
    }

    // Actual formdata value changes
    const handleChange = (index: number, name: string, value: any) => {
        const clonedFormData = cloneDeep(formData)
        clonedFormData[index][name] = value;

        setFormData(clonedFormData)
    }

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const formatOptionLabel = ({ label, description }: any) => (
        <div className="flex flex-col">
            <span className="font-interSemiBold text-sm text-[#344054]">{label}</span>
            <span className="text-[#475467] text-sm">{description}</span>
        </div>
    );

    const save = async () => {
        setSaveBtnDisabled(false)
        const currentTaskId = selectedCardDetail?.['tasks'].find((tsk: any) => tsk.title.toLowerCase() == ACTUAL_TASK_NAMES.LOGISTICS_AND_OTHERS.FINALIZE_LOGISTICS.toLowerCase())?._id;

        const currentTeam = JSON.parse(localStorage.getItem('selectedTeam') as string);

        const originalFormData = cloneDeep(formData);
        originalFormData.map((od: any, index: number) => {
            od['isSaved'] = true;
            od['type'] = `Diet Preference ${index + 1}`;
            delete od.totalDays;
            delete od.isEntireDay;
            delete od.showDropdownData;
            delete od?.isViewMode;
            delete od?.isInit;
            delete od.showVendorDropdown;
            delete od?.vendorOptions;
        })

        const messagePayload = {
            content: JSON.stringify({ 'isLogistics': true, 'items': { dietary_requirements: originalFormData } }),
            teamId: currentTeam?.currentTeamId,
            eventId,
            is_draft: false,
            taskId: currentTaskId,  // Draft Invitation Task
            cardId: selectedCardDetail?._id,
            blockId: selectedCardDetail?.columnId || selectedCardDetail?.block_id
        }

        actualFormData.current = cloneDeep(formData)
        areArraysEqual(formData, cloneDeep(formData))
        setEventDataState({ unsavedChanges: false })

        try {
            const { data } = await updateLogistics(messagePayload)
            updateLogisticsState({ type: 'dietary_requirements', data: formData, updateRag: true, ragId: data?.ragId });

            if (isViewMode) {
                const updatedFormData = cloneDeep(formData).map((fd: any, index: number) => ({ ...fd, isViewMode: true, type: `Diet Preference ${index + 1}`, isSaved: true }))
                setFormData(updatedFormData)
                actualFormData.current = cloneDeep(updatedFormData)
                updateLogisticsState({ type: 'dietary_requirements', data: updatedFormData, updateRag: false, ragId: data?.ragId });
            }
        } catch (error) {
            handleApiCases(false)
        }
        handleApiCases(true)
    }

    const handleApiCases = (isSuccess: boolean) => {
        if (isSuccess) {
            Toast.success('', 'Saved successfully')
        } else {
            Toast.error('', 'Failed to save')
        }
    }

    // const reset = () => {
    //     setFormData([initFormData])
    // }

    const handleOptionChange = (formDataIndex: number, optionIndex: number, arrayName: string, isChecked: boolean) => {
        const clonedFormData: any = cloneDeep(formData)
        const clonedOptions = clonedFormData[formDataIndex][arrayName];
        clonedOptions[optionIndex]['selected'] = isChecked;
        clonedFormData[formDataIndex][arrayName] = clonedOptions;
        setFormData(clonedFormData)
    }

    const edit = (index: any) => {
        const clonedFormData = cloneDeep(formData);
        clonedFormData[index]['isViewMode'] = false
        setFormData(clonedFormData);
        actualFormData.current = clonedFormData
    }

    return (
        <>
            <div id="new-elements" className="h-[calc(100vh_-_440px)] overflow-y-scroll transparent-scrollbar pt-3.5">
                {
                    formData.map((fItem: any, fIndex: any) => (
                        <div key={fIndex} className={`${showAddNewButton ? '' : 'border border-[#EAECF0] bg-[#f2f4f7]'} rounded-lg mb-5 last:mb-0 px-4 py-4 relative w-[97%]`}>
                            {
                                !fItem?.isViewMode && fIndex != 0 && <span onClick={() => remove(fIndex)} className="absolute -top-4 -right-4 border border-[#F2F4F7] rounded-full p-1 cursor-pointer bg-white hover:bg-[#f2f4f7]">
                                    <img className="" src={allImgPaths.crossIcon} alt="" />
                                </span>
                            }
                            {
                                !fItem.isViewMode &&
                                <>
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {/* Type of Cuisine */}
                                        <div className="mb-4">
                                            <p className="text-[#101828] font-interSemiBold mb-4">Type of Cuisine you would like
                                                <span className="text-[#667085]"> (select all that apply)</span>
                                            </p>

                                            <div className="flex flex-wrap gap-y-4 checkbox-alignment">
                                                {
                                                    fItem.cuisineTypes.map((item: any, idx: number) => {
                                                        return (
                                                            <React.Fragment key={idx}>
                                                                <CustomCheckBox
                                                                    name={'cuisine' + idx.toString() + fIndex.toString()}
                                                                    label={item.listName}
                                                                    customLabelStyle="font-interMedium text-sm text-[#344054] pl-2"
                                                                    customStyle="w-1/2"
                                                                    checked={item.selected}
                                                                    onChange={(name: string, isChecked: boolean) => handleOptionChange(fIndex, idx, 'cuisineTypes', isChecked)}
                                                                />
                                                            </React.Fragment>
                                                        )
                                                    })
                                                }
                                            </div>
                                        </div>

                                        {/* Dietary Restrictions  */}
                                        <div className="mb-4">
                                            <p className="text-[#101828] font-interSemiBold mb-4">Types of Dietary Restrictions to be considered
                                                <span className="text-[#667085]"> (select all that apply)</span>
                                            </p>

                                            <div className="flex flex-wrap gap-y-4 checkbox-alignment">
                                                {
                                                    fItem.dietaryRestrictions.map((item: any, idx: number) => {
                                                        return (
                                                            <React.Fragment key={idx}>
                                                                <CustomCheckBox
                                                                    name={'restrictions' + idx.toString() + fIndex.toString()}
                                                                    label={item.listName}
                                                                    customLabelStyle="font-interMedium text-sm text-[#344054] pl-2"
                                                                    customStyle="w-1/2"
                                                                    checked={item.selected}
                                                                    onChange={(name: string, isChecked: boolean) => handleOptionChange(fIndex, idx, 'dietaryRestrictions', isChecked)}
                                                                />
                                                            </React.Fragment>
                                                        )
                                                    })}
                                            </div>
                                        </div>

                                        {/* Beverage Service */}
                                        <div className="">
                                            <p className="text-[#101828] font-interSemiBold mb-4">Beverage Service
                                                <span className="text-[#667085]"> (select all that apply)</span>
                                            </p>

                                            <div className="flex flex-wrap gap-y-4 checkbox-alignment">
                                                {
                                                    fItem.beverageServices.map((item: any, idx: number) => {
                                                        return (
                                                            <React.Fragment key={idx}>
                                                                <CustomCheckBox
                                                                    name={'beverage' + idx.toString() + fIndex.toString()}
                                                                    label={item.listName}
                                                                    customLabelStyle="font-interMedium text-sm text-[#344054] pl-2"
                                                                    customStyle="w-1/2"
                                                                    checked={item.selected}
                                                                    onChange={(name: string, isChecked: boolean) => handleOptionChange(fIndex, idx, 'beverageServices', isChecked)}
                                                                />
                                                            </React.Fragment>
                                                        )
                                                    })}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-5">
                                        <p className="text-[#344054] text-sm font-interMedium pb-1.5">
                                            Special requirements
                                            <span className="text-[#667085]"> (if any)</span>
                                        </p>
                                        <div className="relative ">
                                            <div className="">
                                                <InputTextField
                                                    className="placeholder:text-[#344054] placeholder:text-base text-base text-[#344054]"
                                                    name={"type"}
                                                    placeholder={"Enter any special requirements or needs"}
                                                    onChange={(e: any) => handleChange(fIndex, 'special_requirements', e.target.value)}
                                                    value={fItem.special_requirements}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mb-5">
                                        <p className='text-[#344054] text-sm font-interMedium pb-1.5'>
                                            Use for
                                        </p>
                                        <div className="relative">
                                            <InputTextField
                                                className="placeholder:text-[#344054] placeholder:text-base text-base text-[#344054] w-full"
                                                name={"use_for"}
                                                placeholder={"Entire Event"}
                                                onClick={() => handleDropdownData(fIndex)}
                                                value={fItem.use_for}
                                                onChange={() => { }}
                                            />

                                            {/* Pin Dropdown */}
                                            <div className={`${fItem?.showDropdownData ? "block" : "hidden"} rounded-lg px-3 py-3 shadow-lg absolute bg-[#fff] z-20 w-full max-h-[250px] overflow-y-auto`}>
                                                <div className="bg-[#F9FAFB] rounded px-3 py-3 mb-3">
                                                    <div className='rfp-custom-checkbox'>
                                                        <input
                                                            type='checkbox'
                                                            name={'entireDay' + fIndex}
                                                            checked={fItem.isEntireDay}
                                                            onChange={(e) => handleCheckboxOne(e.target.checked, fIndex)}
                                                            style={{ width: '16px', height: '18px', cursor: 'pointer' }}
                                                            id="entireDay"
                                                        />
                                                        <label
                                                            htmlFor="entireDay"
                                                            className={`font-interMedium`}
                                                        >
                                                            <span className={`font-interMedium text-[#344054] pl-2`}>Entire Event</span>
                                                        </label>
                                                    </div>
                                                </div>

                                                {
                                                    fItem.totalDays.length > 1 &&
                                                    <div className="">
                                                        {
                                                            fItem.totalDays.map((item: any, index: any) => (
                                                                <div className='rfp-custom-checkbox bg-[#F9FAFB] mb-3 last:mb-0 px-3 py-3' key={index}>
                                                                    <input
                                                                        type='checkbox'
                                                                        name={'days' + index + fIndex}
                                                                        checked={item.isChecked}
                                                                        onChange={(e) => handleCheckboxTwo(index, e.target.checked, fIndex)}
                                                                        style={{ width: '16px', height: '18px', cursor: 'pointer' }}
                                                                        id={index}
                                                                    />
                                                                    <label
                                                                        htmlFor={index}
                                                                        className={`font-interMedium`}
                                                                    >
                                                                        <span className={`font-interMedium text-[#344054] pl-2`}>{item.title}</span>
                                                                    </label>
                                                                </div>
                                                            ))
                                                        }
                                                    </div>
                                                }

                                            </div>

                                        </div>
                                        <p className="text-sm tex-[#475467] pt-2">When/how will this be used</p>
                                    </div>

                                    <div className="flex gap-x-4 items-center mb-4">
                                        <CustomCheckBox
                                            name={'include_in_rfp' + fIndex}
                                            label={"Include in RFP for"}
                                            customLabelStyle="font-interMedium text-base text-[#344054] pl-2 flex-shrink-0"
                                            checked={fItem.include_in_rfp}
                                            customStyle="flex-shrink-0"
                                            onChange={(name: string, isChecked: boolean) => handleRFPChange(fIndex, 'include_in_rfp', isChecked)}
                                        />

                                        <div className="relative w-full">
                                            <InputTextField
                                                className="placeholder:text-[#344054] placeholder:text-base text-base text-[#344054] w-full"
                                                name={"vendor" + fIndex}
                                                placeholder={"Select Vendor"}
                                                onClick={() => handleVendorDropdownData(fIndex)}
                                                value={fItem.vendor.join(', ')}
                                                onChange={() => { }}
                                            />

                                            <div className={`${fItem?.showVendorDropdown ? "block" : "hidden"} rounded-lg px-3 py-3 shadow-lg absolute bg-[#fff] z-20 w-full max-h-[250px] overflow-y-auto`}>
                                                {
                                                    fItem.vendorOptions.map((item: any, index: any) => (
                                                        <div className='rfp-custom-checkbox bg-[#F9FAFB] mb-3 last:mb-0 px-3 py-3' key={index}>
                                                            <input
                                                                type='checkbox'
                                                                name={'vendor' + index + fIndex}
                                                                checked={item.selected}
                                                                onChange={(e) => handleVendorSelection(index, e.target.checked, fIndex)}
                                                                style={{ width: '16px', height: '18px', cursor: 'pointer' }}
                                                                id={'vendor' + index + fIndex}
                                                            />
                                                            <label
                                                                htmlFor={'vendor' + index + fIndex}
                                                                className={`font-interMedium`}
                                                            >
                                                                <span className={`font-interMedium text-[#344054] pl-2`}>{item.title}</span>
                                                            </label>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <CustomCheckBox
                                            name={'check_requirement' + fIndex}
                                            label={"Check requirement in attendee RSVP form"}
                                            customLabelStyle="font-interMedium text-base text-[#344054] pl-2"
                                            checked={fItem.check_requirement}
                                            onChange={(name: string, isChecked: boolean) => handleChange(fIndex, 'check_requirement', isChecked)}
                                        />
                                    </div>
                                </>
                            }
                            {
                                fItem?.isViewMode &&
                                <LogisticsViewMode data={fItem} index={fIndex} tab={{ name: 'dietary_requirements' }} remove={remove} edit={edit} add={handleAddMore} />
                            }
                        </div>
                    ))
                }
            </div>
            {
                !showAddNewButton &&
                <div className='flex justify-center mt-4'>
                    <button onClick={handleAddMore} className="border border-[#1E0AF5] hover:bg-[#E8E6FE] rounded-lg flex items-center gap-x-2 py-2.5 px-3.5 mb-6 mt-1.5" type="button">
                        <img src={allImgPaths.plusCircleBlueIcon} alt='' />
                        <span className='text-[#1E0AF5] text-sm font-interSemiBold'>
                            Add More
                        </span>
                    </button>
                </div>
            }

            <div className="modal-footer pt-5 px-6 pb-5 border-t border-[#E4E7EC] -mx-6">
                <div className="flex items-center gap-x-3 justify-end">
                    {/* <button
                        type="button"
                        onClick={reset}
                        className="text-[#344054] font-interSemiBold border border-[#D0D5DD] rounded-lg tracking-wide py-2.5 px-5 !text-base hover:bg-[#F2F4F7]"
                    >
                        Reset
                    </button> */}
                    {
                        !showAddNewButton &&
                        < button
                            className={`!text-base text-white bg-[#1E0AF5] hover:bg-[#120693] font-interSemiBold rounded-lg px-5 py-2.5 border ${saveBtnDisabled ? 'button-disabled' : ''}`}
                            onClick={save}
                            disabled={saveBtnDisabled}
                        >
                            Save
                        </button>
                    }
                </div>
            </div >
        </>
    );
}

export default DietaryRequirements;
